import { useState } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { ApiError, legacyAppUrl } from '@/lib/api'
import {
  Alert,
  Empty,
  Kpi,
  KpiGrid,
  Muted,
  PageHeader,
  Section,
  StatusBadge,
  Tabs,
} from '@/components/ui'
import {
  checkinEmocional,
  fetchClimaPesquisas,
  fetchHeatmapEmocional,
  responderPesquisaClima,
} from './api'

type TabId = 'pesquisas' | 'bemestar'

const EMOCOES = [
  { id: 'otimo', label: 'Ótimo' },
  { id: 'bem', label: 'Bem' },
  { id: 'neutro', label: 'Neutro' },
  { id: 'cansado', label: 'Cansado' },
  { id: 'estressado', label: 'Estressado' },
  { id: 'ansioso', label: 'Ansioso' },
  { id: 'triste', label: 'Triste' },
  { id: 'frustrado', label: 'Frustrado' },
] as const

export function ClimaPage() {
  const qc = useQueryClient()
  const [tab, setTab] = useState<TabId>('pesquisas')
  const [filtro, setFiltro] = useState('ativo')
  const [emocao, setEmocao] = useState('bem')
  const [intensidade, setIntensidade] = useState(5)
  const [comentario, setComentario] = useState('')
  const [anonimo, setAnonimo] = useState(true)
  const [msg, setMsg] = useState('')
  const [notaRapida, setNotaRapida] = useState<Record<string, string>>({})

  const pesquisas = useQuery({
    queryKey: ['clima', 'pesquisas', filtro],
    queryFn: () => fetchClimaPesquisas(filtro || undefined),
  })
  const heatmap = useQuery({
    queryKey: ['rede', 'heatmap'],
    queryFn: () => fetchHeatmapEmocional(30),
  })

  const responder = useMutation({
    mutationFn: ({ id, nota }: { id: string; nota: string }) =>
      responderPesquisaClima(id, { nota, origem: 'portal-react' }),
    onSuccess: async () => {
      setMsg('Resposta registrada. Obrigado!')
      await qc.invalidateQueries({ queryKey: ['clima', 'pesquisas'] })
    },
    onError: (err) => {
      setMsg(err instanceof ApiError ? err.message : 'Falha ao responder.')
    },
  })

  const checkin = useMutation({
    mutationFn: () =>
      checkinEmocional({
        emocao,
        intensidade,
        comentario: comentario.trim() || undefined,
        anonimo,
        contexto: 'trabalho',
      }),
    onSuccess: async () => {
      setComentario('')
      setMsg('Check-in emocional registrado.')
      await qc.invalidateQueries({ queryKey: ['rede', 'heatmap'] })
    },
    onError: (err) => {
      setMsg(err instanceof ApiError ? err.message : 'Falha no check-in.')
    },
  })

  const lista = pesquisas.data?.dados || []

  return (
    <section className="hi-page">
      <PageHeader
        title="Clima & engajamento"
        subtitle="Pesquisas de clima (RH) e check-in emocional da Rede Corporativa."
        actions={
          <a className="btn btn-secondary" href={legacyAppUrl('#/vibracao-corporativa')}>
            Legado
          </a>
        }
      />

      {msg ? <Alert tone="info">{msg}</Alert> : null}

      <KpiGrid>
        <Kpi label="Pesquisas listadas" value={String(lista.length)} />
        <Kpi label="Ativas" value={String(pesquisas.data?.ativas ?? '—')} />
        <Kpi
          label="Bem-estar (30d)"
          value={
            heatmap.data?.media_bem_estar != null
              ? String(heatmap.data.media_bem_estar)
              : '—'
          }
        />
      </KpiGrid>

      <Tabs
        label="Seções de clima"
        value={tab}
        onChange={setTab}
        items={[
          { id: 'pesquisas', label: 'Pesquisas' },
          { id: 'bemestar', label: 'Bem-estar' },
        ]}
      />

      {tab === 'pesquisas' ? (
        <>
          <form
            className="hi-filters"
            onSubmit={(e) => {
              e.preventDefault()
            }}
          >
            <select
              value={filtro}
              onChange={(e) => setFiltro(e.target.value)}
              aria-label="Filtrar pesquisas"
            >
              <option value="ativo">Ativas</option>
              <option value="rascunho">Rascunho</option>
              <option value="encerrado">Encerradas</option>
              <option value="">Todas</option>
            </select>
          </form>

          <Section title="Pesquisas de clima" flush>
            {pesquisas.isLoading ? <Muted>Carregando pesquisas…</Muted> : null}
            {pesquisas.isError ? (
              <Alert tone="danger">
                {pesquisas.error instanceof ApiError
                  ? pesquisas.error.message
                  : 'Falha ao listar pesquisas.'}
              </Alert>
            ) : null}
            {!pesquisas.isLoading && lista.length === 0 ? (
              <Empty>Nenhuma pesquisa neste filtro. Crie no legado RH → Clima.</Empty>
            ) : null}
            <ul className="hi-list">
              {lista.map((p) => (
                <li key={p.id} className="card hi-row" style={{ alignItems: 'flex-start' }}>
                  <div className="hi-row-main" style={{ width: '100%' }}>
                    <div className="hi-actions" style={{ justifyContent: 'space-between' }}>
                      <strong>{p.titulo}</strong>
                      <StatusBadge
                        tone={
                          p.status === 'ativo'
                            ? 'ativo'
                            : p.status === 'rascunho'
                              ? 'pendente'
                              : 'inativo'
                        }
                      >
                        {p.status_display || p.status || '—'}
                      </StatusBadge>
                    </div>
                    <Muted>
                      {p.tipo_display || p.tipo || 'clima'}
                      {p.anonima ? ' · anônima' : ' · identificada'}
                      {` · ${p.total_respostas ?? 0} respostas`}
                    </Muted>
                    {p.descricao ? <p className="hi-muted">{p.descricao}</p> : null}
                    {p.status === 'ativo' ? (
                      <form
                        className="hi-form-stack"
                        style={{ marginTop: 8 }}
                        onSubmit={(e) => {
                          e.preventDefault()
                          const nota = (notaRapida[p.id] || '').trim()
                          if (!nota) {
                            setMsg('Informe uma nota ou comentário rápido.')
                            return
                          }
                          responder.mutate({ id: p.id, nota })
                        }}
                      >
                        <input
                          type="text"
                          placeholder="Resposta rápida (nota / comentário)"
                          value={notaRapida[p.id] || ''}
                          onChange={(e) =>
                            setNotaRapida((d) => ({ ...d, [p.id]: e.target.value }))
                          }
                          aria-label={`Responder pesquisa ${p.titulo}`}
                        />
                        <button
                          className="btn btn-primary btn-sm"
                          type="submit"
                          disabled={responder.isPending}
                        >
                          Enviar resposta
                        </button>
                      </form>
                    ) : null}
                  </div>
                </li>
              ))}
            </ul>
          </Section>
        </>
      ) : null}

      {tab === 'bemestar' ? (
        <Section title="Check-in emocional">
          {heatmap.isError ? (
            <Alert tone="warning">
              Heatmap indisponível.{' '}
              {heatmap.error instanceof ApiError ? heatmap.error.message : ''}
            </Alert>
          ) : (
            <Muted>
              Média de bem-estar (últimos {heatmap.data?.dias ?? 30} dias):{' '}
              <strong>
                {heatmap.data?.media_bem_estar != null
                  ? heatmap.data.media_bem_estar
                  : '—'}
              </strong>
            </Muted>
          )}
          <form
            className="hi-form-stack"
            style={{ marginTop: 12 }}
            onSubmit={(e) => {
              e.preventDefault()
              checkin.mutate()
            }}
          >
            <label>
              Como você está?
              <select
                value={emocao}
                onChange={(e) => setEmocao(e.target.value)}
                aria-label="Emoção do check-in"
              >
                {EMOCOES.map((em) => (
                  <option key={em.id} value={em.id}>
                    {em.label}
                  </option>
                ))}
              </select>
            </label>
            <label>
              Intensidade ({intensidade})
              <input
                type="range"
                min={1}
                max={10}
                value={intensidade}
                onChange={(e) => setIntensidade(Number(e.target.value))}
                aria-label="Intensidade emocional"
              />
            </label>
            <textarea
              rows={2}
              placeholder="Comentário opcional (privado)"
              value={comentario}
              onChange={(e) => setComentario(e.target.value)}
              aria-label="Comentário do check-in"
            />
            <label className="hi-check">
              <input
                type="checkbox"
                checked={anonimo}
                onChange={(e) => setAnonimo(e.target.checked)}
              />{' '}
              Enviar de forma anônima
            </label>
            <button className="btn btn-primary" type="submit" disabled={checkin.isPending}>
              {checkin.isPending ? 'Enviando…' : 'Registrar check-in'}
            </button>
          </form>
        </Section>
      ) : null}
    </section>
  )
}
